// Help text for: Live tab: Diagram, Table, Distribution and Forecasting views.
// Keys are referenced as "live.<field>" by <FieldHelp id=... />. Entry shape:
//   title  - field name as shown on screen
//   use    - what the field is for / how to read it
//   source - measured | calculated | backend | default | setting | input
//   how    - where the value comes from: formula, default value, or who sets it
const help = {
  // ===== Diagram view: header =====
  totalLoad: {
    title: "Total Load",
    use: "How much power everything in the branch you are viewing is drawing right now, added together.",
    source: "calculated",
    how: "If the item at the top has its own meter, that reading is used. If it is a grouping item with no meter, the readings underneath are added up, with solar and generators counted as the power they supply. Shown in kilowatts (kW) and follows the branch selector.",
  },
  operatingCost: {
    title: "Operating Cost / hr",
    use: "What it would cost to keep running at this load for one hour.",
    source: "calculated",
    how: "Total Load multiplied by your grid unit price (PKR per kWh). The price is the one saved on your account when you signed in, set under Info > Details > Unit Prices. Shows -- when no price is set or the load is zero.",
  },
  countNormal: {
    title: "Normal",
    use: "How many machines in this branch are running comfortably inside the power they are rated for.",
    source: "calculated",
    how: "Counts the running machines whose live power is below 95% of their rated power. Grouping items and the item at the top are not counted.",
  },
  countWarning: {
    title: "Warning",
    use: "How many machines are running close to the power they are rated for.",
    source: "calculated",
    how: "Counts the running machines whose live power sits between 95% and 100% of their rated power, as read at their breakers.",
  },
  countCritical: {
    title: "Critical",
    use: "How many machines are pushing past their rated power and need a look.",
    source: "calculated",
    how: "Counts the running machines whose live power is above 100% of their rated power.",
  },
  countOn: {
    title: "On",
    use: "How many machines are running right now.",
    source: "calculated",
    how: "Counts machines drawing more than 0.05 kW (0.5 kW for generators), read at their breakers. Grouping items and the item at the top are left out. Machines with no health figure, for example because no rated power is set, still count here.",
  },
  countOff: {
    title: "Off",
    use: "How many machines are switched off or not sending readings.",
    source: "calculated",
    how: "Counts machines drawing 0.05 kW or less (0.5 kW for generators). A breaker with no reading for 10 minutes reads 0 and lands here too.",
  },

  // ===== Diagram view: node cards =====
  nodeCategory: {
    title: "Node type",
    use: "What kind of item this is: main (grid) supply, solar, generator, a machine or load, or a grouping item that only holds others.",
    source: "setting",
    how: "The type chosen for it when it was added to your plant setup.",
  },
  nodePower: {
    title: "Power",
    use: "How much power this item is drawing, or supplying, right now, in kW.",
    source: "measured",
    how: "Its latest breaker reading, shown as a positive number; a main that is sending power out is marked EXPORT. A grouping item with no meter of its own adds up the readings beneath it, counting solar and generators as the power they supply.",
  },
  offlineBadge: {
    title: "Offline",
    use: "This item has stopped sending readings, so what you see is not live.",
    source: "backend",
    how: "Appears when no reading has arrived from that breaker for 10 minutes. Its power then counts as 0 kW.",
  },
  exportBadge: {
    title: "Export",
    use: "Power is flowing out through this main meter, back to the grid.",
    source: "calculated",
    how: "Appears on a main (grid) item when its reading drops below -0.05 kW, that is, turns negative.",
  },
  offStatus: {
    title: "Off",
    use: "The machine is not drawing real power at the moment, so no health figure is shown.",
    source: "calculated",
    how: "Counts as off when its power is 0.05 kW or less (0.5 kW for generators), including breakers that have sent nothing for 10 minutes.",
  },
  childCounts: {
    title: "Nodes below",
    use: "A quick count of the machines under this item: total / on / off. Click it to open or close the branch.",
    source: "calculated",
    how: "Counts every real machine below it, however deep. On means drawing more than 0.05 kW (0.5 kW for generators); off means at or below that, including anything not sending readings.",
  },
  insightValue: {
    title: "Insight Value",
    use: "A reference number recorded for this machine, shown exactly as it was typed in.",
    source: "setting",
    how: "Saved with the machine's settings by your administrator. It is not worked out from live readings.",
  },

  // ===== Diagram view: details panel =====
  tabularView: {
    title: "Tabular View",
    use: "Shows this item's latest readings as a plain list of values.",
    source: "input",
    how: "Your choice for this panel. It goes back to Tabular View every time the panel is closed.",
  },
  linearView: {
    title: "Linear View",
    use: "Shows this item's readings as a live line chart of the last 10 minutes.",
    source: "input",
    how: "Your choice for this panel. The chart covers the 10 minutes up to the newest reading and redraws every 15 seconds; the values are worked out exactly as in the list.",
  },
  nodeType: {
    title: "Type",
    use: "Whether this item supplies power (grid, solar or generator) or uses it.",
    source: "setting",
    how: "Taken from the type set for it in your plant setup: grid, solar and generator are named as such, and everything else shows as LOAD.",
  },
  multiParent: {
    title: "Multi-Parent",
    use: "This item is fed from, or belongs under, more than one parent.",
    source: "setting",
    how: "Appears when your plant setup lists more than one parent for it. How many parents it has is shown in the details panel.",
  },
  sharedBranch: {
    title: "Shared",
    use: "This row is a second showing of an item that sits under more than one parent.",
    source: "calculated",
    how: "Appears when the item shows up under a parent that is not its main one. It is the same breaker as the first row, so do not add the two together.",
  },
  onOffStatus: {
    title: "Status",
    use: "Whether the machine is running (ON) or not (OFF).",
    source: "calculated",
    how: "OFF when its power is 0.05 kW or less (0.5 kW for generators), or its breaker has sent nothing for 10 minutes. Otherwise ON.",
  },
  healthStatus: {
    title: "Health Status",
    use: "How hard the machine is working next to the power it is rated for.",
    source: "backend",
    how: "Live power divided by rated power, as a percentage: Normal below 95%, Warning from 95% to 100%, Critical above 100%. Nothing is shown when the machine draws less than 0.5 kW, its breaker is not reporting, or no rated power has been set (set it under Info > Node Settings > Rated Power).",
  },

  // ===== Readings (Table columns and details panel) =====
  machineName: {
    title: "Name",
    use: "The machine or item this row belongs to. The indent shows where it sits in your plant hierarchy.",
    source: "setting",
    how: "The name given to it in your plant setup. Grouping rows are shaded and show only their added-up power.",
  },
  lastUpdated: {
    title: "Last Updated",
    use: "When the newest reading was taken at this breaker.",
    source: "measured",
    how: "The time stamp of the newest reading to arrive in the last 10 minutes. 'More than 10 mins' means nothing has arrived in that window, so the breaker is not reporting.",
  },
  power: {
    title: "Power (kW)",
    use: "The real power the machine is drawing, or a source is supplying, right now.",
    source: "measured",
    how: "The three phase powers added together (one phase on single-phase meters), turned into kilowatts and multiplied by the scale set for that meter. A grouping item shows the sum of everything below it. Blank when the breaker is not reporting.",
  },
  current: {
    title: "Current (A)",
    use: "The electrical current flowing to the machine.",
    source: "measured",
    how: "On three-phase meters, the three phase currents added together and multiplied by the scale set for that meter. Switching Current to Average shows their mean instead. Single-phase meters show their one phase.",
  },
  voltage: {
    title: "Voltage (V)",
    use: "The supply voltage at the machine's breaker.",
    source: "measured",
    how: "On three-phase meters, the three phase voltages averaged and multiplied by 1.732 to give the line-to-line voltage. Single-phase meters show their own phase voltage.",
  },
  powerFactor: {
    title: "Power Factor",
    use: "How well the machine uses the current it draws; 1.00 is ideal. Low values waste capacity and can bring penalties.",
    source: "measured",
    how: "The average of the phase power factors the meter reports, leaving out any that read zero. Green above 0.80, orange from 0.70 to 0.80, red below 0.70.",
  },
  frequency: {
    title: "Frequency (Hz)",
    use: "The frequency of the electrical supply; it should stay close to 50 Hz.",
    source: "measured",
    how: "Reported straight from the meter with every reading.",
  },
  thdVoltage: {
    title: "THD Voltage (%)",
    use: "Total harmonic distortion of the voltage — how far the voltage wave is from a clean sine wave. Lower is better.",
    source: "measured",
    how: "The average of the three phase values the meter reports (single-phase meters show their one phase).",
  },
  thdCurrent: {
    title: "THD Current (%)",
    use: "Total harmonic distortion of the current. High values point to equipment like drives and electronics.",
    source: "measured",
    how: "The average of the three phase values the meter reports (single-phase meters show their one phase).",
  },
  tap: {
    title: "TAP (kW)",
    use: "Total Active Power as the meter itself reports it — a handy cross-check on the Power column.",
    source: "measured",
    how: "The meter's own total active power figure, turned into kilowatts. Unlike Power, the meter's scale is not applied to it.",
  },
  shareOfTotal: {
    title: "% of Total Load",
    use: "How much of the Total Load shown at the top of the diagram this item accounts for.",
    source: "calculated",
    how: "This item's power divided by Total Load, as a percentage (both in kW). Shows 0% when Total Load is zero.",
  },
  phaseDetails: {
    title: "Phase Details",
    use: "Each of the three phases on its own, so you can spot a load that is out of balance.",
    source: "measured",
    how: "Voltage, current, power and power factor per phase from the newest reading. Current and power are multiplied by the scale set for that meter.",
  },

  // ===== Linear view chart =====
  chartPhase: {
    title: "Phase",
    use: "Choose whether the chart shows the combined values or a single phase.",
    source: "input",
    how: "All shows the same combined values as the list. Pick Phase 1, 2 or 3 to see that phase alone; Frequency and TAP have no per-phase value, so they disappear.",
  },

  // ===== Table view =====
  tableTotalUsage: {
    title: "Total Usage",
    use: "The power your whole site is using right now.",
    source: "backend",
    how: "For each top-level part of your hierarchy, the highest metered item on every path is taken, so a parent and its machines are never counted twice; then all solar and generator output is added on. Grouping items are skipped and breakers that are not reporting count as 0. It covers every branch, so it ignores the branch selector.",
  },
  usageTime: {
    title: "As of",
    use: "The time the Total Usage figure was worked out.",
    source: "backend",
    how: "The hours and minutes at the moment the latest refresh came through. This screen refreshes every 15 seconds.",
  },
  metersOffline: {
    title: "Meters offline",
    use: "How many meters are not reporting, so Total Usage may be lower than reality.",
    source: "calculated",
    how: "Counts the real meters with no reading in the last 10 minutes. Each of those counts as 0 kW in Total Usage.",
  },
  tableOperatingCost: {
    title: "Cost per Hour",
    use: "What one more hour at this usage would add to your bill.",
    source: "calculated",
    how: "Total Usage multiplied by your grid unit price (Rs. per kWh), rounded to whole rupees. The price is the one saved on your account when you signed in, set under Info > Details > Unit Prices; with no price set this shows 0.",
  },
  tableViewMode: {
    title: "View",
    use: "Hierarchy nests machines under their parents. Flat lists every machine once and lets you sort by clicking a column heading.",
    source: "input",
    how: "Your choice for this screen; it starts on Hierarchy. Sorting is also available once you pick a single machine in Filter.",
  },
  currentMode: {
    title: "Current: Sum / Average",
    use: "Choose how three-phase current is shown in the Current column.",
    source: "input",
    how: "Sum adds the three phase currents together. Average shows their mean, which compares fairly with a single-phase machine. Single-phase meters read the same either way. Starts on Sum.",
  },
  tableFilter: {
    title: "Filter",
    use: "Narrow the view down to one machine. A three-phase machine then lists its three phases beneath it.",
    source: "input",
    how: "Your pick from the machines in the branch you are in; All Equipment shows everything. Clear brings them all back.",
  },
  meterStatus: {
    title: "Meter Status",
    use: "Whether the meter is sending data.",
    source: "backend",
    how: "Online when a reading has arrived from that breaker in the last 10 minutes, offline when none has. Grouping items have no meter and show -.",
  },

  // ===== Distribution view =====
  distribution: {
    title: "Power Distribution",
    use: "A live picture of where your power comes from: each main, solar and generator meter feeding the site's load in the middle.",
    source: "measured",
    how: "Drawn from the newest readings for the branch picked at the top. Arrows move while a source supplies more than 0.05 kW (0.5 kW for generators), and point outward when a main is exporting. Click a source to show only that one.",
  },
  distSources: {
    title: "Sources",
    use: "How many supply meters (mains, solar, generators and other sources) sit in this branch, and how many you are looking at.",
    source: "calculated",
    how: "Counts everything in the chosen branch that is not a machine, a load, a grouping item or an environment item. 'X of Y' appears once you have clicked a single source to isolate it.",
  },
  distSupply: {
    title: "Supply",
    use: "The power coming from the sources you are showing right now.",
    source: "calculated",
    how: "Their power added together, in kW. Mains keep their sign, so export is negative; solar and generators count as the power they supply. Sources that are not reporting are left out. '(net export)' is added when the total falls below -0.05 kW.",
  },
  distTotalPower: {
    title: "Total power",
    use: "The power used by the branch you picked, or by the whole site when All is picked.",
    source: "calculated",
    how: "All solar and generator output in the branch, plus the highest metered item on every path so a parent and its machines are not counted twice. Grouping items are skipped and breakers that are not reporting count as 0. Isolating a source does not change it.",
  },

  // ===== Forecasting view =====
  forecastChart: {
    title: "Load Forecasting",
    use: "The chosen machine's power over the last 24 hours, plus what it is expected to draw for the next 4 hours.",
    source: "backend",
    how: "The solid line is hourly power from the saved readings. The dashed line is the forecast: a saved trained forecast while it is still accurate and less than 4 hours old, otherwise one fitted fresh on the last 30 days (yesterday at the same hour, the hour of day, and whether it is a weekend). At least 7 days of saved readings are needed.",
  },
  forecastMape: {
    title: "MAPE (forecast error)",
    use: "How far the forecast has missed on average, as a percentage of the real power. Lower is better.",
    source: "backend",
    how: "Mean absolute percentage error, measured by replaying past forecasts against the readings that actually came in (the last 30 days for a freshly fitted forecast). Hours where the real load was 20 kW or less are left out, because a small miss on a tiny load looks huge as a percentage.",
  },
  forecastCalibrating: {
    title: "Calibrating",
    use: "The forecast for this machine is not trustworthy yet, so take the dashed line lightly.",
    source: "backend",
    how: "Appears when the middle value of the forecast's hourly errors reaches 20% or more, which is the point where it stops counting as reliable.",
  },
  forecastMain: {
    title: "Main",
    use: "Choose the parent whose machines you want a forecast for.",
    source: "input",
    how: "Lists the parents in your hierarchy that hold machines a forecast can be made for, with a count for each. Starts on the first one; machines with no parent appear under Ungrouped.",
  },
  forecastMachine: {
    title: "Machine",
    use: "Choose the machine to forecast.",
    source: "input",
    how: "Lists the metered main (grid), solar and machine items under the Main you picked; grouping, environment and generator items cannot be forecast. Starts on the first one.",
  },
};

export default help;
